import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { StarIcon, CheckIcon, XIcon } from 'lucide-react';
import { trackAddToCart } from '../utils/pixel';
export function TestimonialsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const testimonials = [{
    headline: 'Quedé embarazada 6 semanas después de hacer el genograma',
    body: 'Llevaba 3 años de tratamientos, 2 FIV fallidas y un diagnóstico de "causa desconocida". Cuando llegué a El Código del Vientre estaba agotada de buscar respuestas en la medicina. Cuando dibujé mi árbol encontré algo que nadie me había dicho: mi abuela materna había perdido 3 bebés en silencio y nunca los lloró. Hice el ejercicio de devolución de cargas de la meditación y algo se movió en mí. Seis semanas después, el test dio positivo. No sé explicarlo con palabras, pero sé que algo se ordenó.',
    author: 'Valentina R., 34 años',
    location: 'Rosario, Argentina'
  }, {
    headline: 'Por primera vez en años dejé de sentir que era mi culpa',
    body: 'Lo que más me dolía no era el diagnóstico, era la culpa. Sentía que mi cuerpo me fallaba, que yo le fallaba a mi esposo, que Dios me castigaba. La meditación bíblica me quebró por dentro de la mejor manera. Escuchar el versículo de Hebreos 11:11 mientras visualizaba a mis ancestras fue como recibir una absolución que yo no sabía que necesitaba. Todavía estoy en el proceso, pero hoy camino diferente. Con fe, no con miedo.',
    author: 'Carolina M., 38 años',
    location: 'Montevideo, Uruguay'
  }, {
    headline: 'Mi esposo y yo nunca habíamos hablado tan profundo',
    body: 'El Bono 3 fue una sorpresa total. No esperaba que "preparar la casa" fuera tan poderoso. Cuando le expliqué a mi esposo el ejercicio del "Lugar del Padre", él se emocionó y me dijo que sentía que por fin tenía un rol en todo esto. Habíamos estado tan enfocados en mis tratamientos que él se había quedado afuera del proceso. Ese protocolo de 7 días nos unió de una manera que ningún médico ni consejero había logrado.',
    author: 'Luciana P., 31 años',
    location: 'Santiago, Chile'
  }, {
    headline: 'Lo compré con desconfianza y lo terminé llorando de gratitud',
    body: 'Soy médica. Soy racional. Cuando vi este producto pensé que era otra cosa esotérica más. Lo compré casi para descartarlo. Pero la base sistémica del libro tiene fundamentos reales en epigenética y trauma transgeneracional. Y la meditación... la meditación me hizo llorar como no lloraba desde que perdí mi primer embarazo hace 4 años. Ese llanto fue el inicio de algo nuevo. Ya estoy en mi segunda semana de estimulación y mi ginecóloga dice que mis folículos responden diferente. No lo puedo probar científicamente, pero algo cambió.',
    author: 'Daniela F., 40 años',
    location: 'Buenos Aires, Argentina'
  }, {
    headline: 'El árbol familiar me reveló un secreto de 40 años',
    body: 'Con las 15 preguntas del Bono 1 llamé a mi tía que vive en España. Me contó que mi abuela había tenido un hijo antes de casarse con mi abuelo, y que ese bebé murió al nacer y nunca fue nombrado. Nadie en la familia lo sabía. Cuando hice el ejercicio de incluirlo en mi árbol y decirle su nombre, sentí algo físico en mi vientre. Como si algo se soltara. Dos meses después estoy escribiendo esto con 8 semanas de embarazo.',
    author: 'Mariana T., 36 años',
    location: 'Córdoba, Argentina'
  }, {
    headline: 'No soy religiosa, pero la parte espiritual me llegó igual',
    body: 'No vengo de una familia creyente, así que dudé por los versículos bíblicos. Pero la autora explica todo de una manera tan universal que lo pude tomar desde mi propia espiritualidad. Lo que más me impactó fue el concepto del "permiso". Yo nunca me había dado cuenta de que inconscientemente no me daba permiso para ser madre porque mi propia madre nunca fue feliz siéndolo. Eso lo cambió todo para mí.',
    author: 'Sofía B., 29 años',
    location: 'Mendoza, Argentina'
  }];
  const stats = [{
    icon: '⭐',
    label: 'Valoración promedio',
    value: '4.9 / 5 estrellas'
  }, {
    icon: '💛',
    label: 'Mujeres que reportaron calma emocional',
    value: '94%'
  }, {
    icon: '🌱',
    label: 'Parejas que mejoraron su comunicación',
    value: '87%'
  }, {
    icon: '🙏',
    label: 'Usuarias que sintieron un "movimiento interno"',
    value: '91%'
  }, {
    icon: '📖',
    label: 'Tasa de completación del protocolo',
    value: '78%'
  }];
  const socialQuotes = [{
    text: 'Esto no es un libro, es un espejo de tu alma.',
    handle: '@valentina.mama.en.camino'
  }, {
    text: 'Nunca pensé que llorar frente a un árbol dibujado me iba a sanar.',
    handle: '@caro.fe.y.fertilidad'
  }, {
    text: 'Lo recomendé a 3 amigas. Las 3 lo están haciendo juntas.',
    handle: '@soyesposasoymama'
  }];
  return <section ref={ref} className="w-full bg-cream py-6 md:py-10 px-6" data-id="element-241">
      <div className="max-w-5xl mx-auto" data-id="element-242">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} data-id="element-243">
          {/* Risk Comparison */}
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-md border-2 border-gold/30 mb-8" data-id="element-244">
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-brown text-center mb-2" data-id="element-245">
              ¿Seguís dudando?
            </h3>
            <p className="font-sans text-base text-brown/80 text-center mb-6 max-w-2xl mx-auto" data-id="element-246">
              Entendemos. Este camino te ha enseñado a protegerte de las
              decepciones. Pero fijate en esto:
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-6" data-id="element-247">
              <div className="bg-rose/5 rounded-2xl p-5 border border-rose/20" data-id="element-248">
                <h4 className="font-serif text-lg font-bold text-rose mb-3 flex items-center gap-2" data-id="element-249">
                  <CheckIcon className="w-5 h-5" data-id="element-250" /> Si comprás y no te sirve
                </h4>
                <ul className="space-y-2" data-id="element-251">
                  <li className="font-sans text-sm text-brown/80" data-id="element-252">
                    • Pedís reembolso en 30 días
                  </li>
                  <li className="font-sans text-sm text-brown/80" data-id="element-253">
                    • Perdés $0
                  </li>
                  <li className="font-sans text-sm text-brown/80" data-id="element-254">
                    • Tenés garantía total
                  </li>
                </ul>
              </div>
              <div className="bg-brown/5 rounded-2xl p-5 border border-brown/20" data-id="element-255">
                <h4 className="font-serif text-lg font-bold text-brown/70 mb-3 flex items-center gap-2" data-id="element-256">
                  <XIcon className="w-5 h-5" data-id="element-257" /> Si no comprás y esto era lo que
                  necesitabas
                </h4>
                <ul className="space-y-2" data-id="element-258">
                  <li className="font-sans text-sm text-brown/70" data-id="element-259">
                    • Seguís esperando sin saber por qué
                  </li>
                  <li className="font-sans text-sm text-brown/70" data-id="element-260">
                    • Perdés otra oportunidad de sanar
                  </li>
                  <li className="font-sans text-sm text-brown/70" data-id="element-261">
                    • No tenés nada nuevo que intentar
                  </li>
                </ul>
              </div>
            </div>

            <p className="font-serif text-lg md:text-xl text-brown font-semibold text-center italic" data-id="element-262">
              El riesgo real no es comprar. El riesgo real es no intentarlo.
            </p>
          </div>

          {/* Final CTA */}
          <div className="text-center" data-id="element-263">
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => {
            trackAddToCart();
            window.open('https://www.inerciacero.com/cart/42837827551335:1', '_blank');
          }} className="bg-gradient-to-r from-rose to-gold text-white font-sans font-bold text-lg md:text-xl px-10 md:px-16 py-5 md:py-6 rounded-full shadow-2xl hover:shadow-xl transition-shadow cursor-pointer" data-id="element-264">
              SÍ, QUIERO SANAR MI HISTORIA Y ABRIR PASO A LA VIDA
            </motion.button>
            <p className="font-sans text-sm text-brown/70 mt-4" data-id="element-265">
              🔐 Acceso inmediato · $14.00 USD · Garantía de 30 días
            </p>
            <blockquote className="mt-5 max-w-xl mx-auto" data-id="element-266">
              <p className="font-serif text-base md:text-lg text-brown/70 italic" data-id="element-267">
                "Porque por la fe Sara también recibió fuerza para concebir."
              </p>
              <cite className="font-sans text-sm text-gold font-medium not-italic" data-id="element-268">
                — Hebreos 11:11
              </cite>
            </blockquote>
          </div>
        </motion.div>
      </div>
    </section>;
}